import { useEffect, useState } from 'react';
import { PageTitle } from '../../../_metronic/layout/core';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Alert,
  Snackbar,
  AlertTitle,
  CircularProgress,
  Avatar,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  EditNote as EditNoteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getCertificadoPlantillas,
  deleteCertificadoPlantilla,
  postCertificadoPlantillaDuplicar,
  putCertificadoPlantillaVigente,
} from '../../modules/apps/user-management/users-list/core/_requests';
import UseDidMountEffect from '../../modules/auth/core/UseDidMountEffect';
import BackButton from '../../components/BackButton/BackButton';

interface UserComplemento {
  primer_nome: string;
  segundo_nome: string;
  primer_apelido: string;
  segundo_apelido: string;
}

interface CreatedByUser {
  id: number;
  email: string;
  nombreCompleto: string;
  complemento: UserComplemento;
}

interface UpdatedByUser {
  id: number;
  email: string;
  nombreCompleto: string;
  complemento: UserComplemento;
}

interface PlantillaCertificado {
  id: number;
  nombre_plantilla?: string;
  titulo_certificado?: string;
  entidad?: string;
  tipo_certificado?: string;
  vigente: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: number;
  createdByUser?: CreatedByUser;
  updatedBy?: number;
  updatedByUser?: UpdatedByUser;
}

const CertificadoPlantillaPage = () => {
  const navigate = useNavigate();
  const [plantillas, setPlantillas] = useState<PlantillaCertificado[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  });

  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    fetchPlantillas();
  }, []);

  UseDidMountEffect(() => {
    fetchPlantillas();
  }, [searchTerm]);

  const fetchPlantillas = async () => {
    try {
      setLoading(true);
      const response = await getCertificadoPlantillas({
        search: searchTerm,
        limit: 100,
      });
      
      let plantillasData: PlantillaCertificado[] = [];
      
      if (response && response.data && response.data.data && response.data.data.plantillas) {
        plantillasData = response.data.data.plantillas;
      } else if (response && response.data && response.data.plantillas) {
        plantillasData = response.data.plantillas;
      } else if (response && response.data && Array.isArray(response.data)) {
        plantillasData = response.data;
      }
      
      // Ordenar: Primero la vigente, luego las demás por ID descendente
      const plantillasOrdenadas = [...plantillasData].sort((a, b) => {
        if (a.vigente && !b.vigente) return -1;
        if (!a.vigente && b.vigente) return 1;
        return b.id - a.id;
      });
      
      setPlantillas(plantillasOrdenadas);
    } catch (error: any) {
      console.error('Error fetching plantillas:', error);
      showAlert(false, error.response?.data?.message || 'Error al cargar las plantillas');
      setPlantillas([]);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (nombreCompleto: string) => {
    if (!nombreCompleto) return '??';
    const words = nombreCompleto.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return nombreCompleto.substring(0, 2).toUpperCase();
  };

  const formatFecha = (fecha: string) => {
    if (!fecha) return '-';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatHora = (fecha: string) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const showAlert = (success: boolean, message: string) => {
    setAlert(success);
    setAlertMsg(message);
    setOpen(true);
    setTimeout(() => {
      setOpen(false);
    }, 3000);
  };

  const handleCreate = () => {
    navigate('/admin/certificado-plantilla/crear');
  };

  const handleEdit = (id: number) => {
    navigate(`/admin/certificado-plantilla/editar/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/admin/certificado-plantilla/vista-previa/${id}`);
  };

  const handleDuplicate = async (id: number) => {
    try {
      await postCertificadoPlantillaDuplicar(id);
      showAlert(true, 'Plantilla duplicada exitosamente');
      fetchPlantillas();
    } catch (error: any) {
      console.error('Error duplicating plantilla:', error);
      showAlert(false, error.response?.data?.message || 'Error al duplicar la plantilla');
    }
  };

  const handleSetVigente = async (id: number) => {
    try {
      await putCertificadoPlantillaVigente(id);
      showAlert(true, 'Plantilla marcada como vigente');
      fetchPlantillas();
    } catch (error: any) {
      console.error('Error setting vigente:', error);
      showAlert(false, error.response?.data?.message || 'Error al marcar como vigente');
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.id) {
      try {
        await deleteCertificadoPlantilla(deleteDialog.id);
        showAlert(true, 'Plantilla eliminada exitosamente');
        fetchPlantillas();
      } catch (error: any) {
        console.error('Error deleting plantilla:', error);
        showAlert(false, error.response?.data?.message || 'Error al eliminar la plantilla');
      }
    }
    setDeleteDialog({ open: false, id: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
        <Box sx={{ mb: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
            <BackButton />
            <div>
              <h2>Gestionar Plantilla de Certificación</h2>
              <p style={{ color: '#666', margin: 0 }}>
                Administra las plantillas de certificados de formación
              </p>
            </div>
          </div>
        </Box>

        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            mb: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={9}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por nombre de plantilla"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreate}
              >
                Nueva Plantilla
              </Button>
            </Grid>
          </Grid>
        </Toolbar>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px' }}>
                  <h4>Nombre</h4>
                </TableCell>
                <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px' }}>
                  <h4>Título/Tipo</h4>
                </TableCell>
                <TableCell align="center" style={{ fontFamily: 'Poppins', fontSize: '16.25px' }}>
                  <h4>Estado</h4>
                </TableCell>
                <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px' }}>
                  <h4>Última Actualización</h4>
                </TableCell>
                <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px' }}>
                  <h4>Creado/Actualizado Por</h4>
                </TableCell>
                <TableCell align="center" style={{ fontFamily: 'Poppins', fontSize: '16.25px' }}>
                  <h4>Acciones</h4>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : !plantillas || plantillas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No se encontraron plantillas
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                plantillas.map((plantilla) => {
                  const esVigente = plantilla.vigente;
                  
                  return (
                    <TableRow 
                      key={plantilla.id} 
                      hover
                      sx={{
                        backgroundColor: esVigente ? 'rgba(76, 175, 80, 0.05)' : 'inherit',
                      }}
                    >
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
                        {plantilla.nombre_plantilla || plantilla.entidad || 'Sin nombre'}
                        {esVigente && (
                          <Chip 
                            label="ACTUAL" 
                            size="small" 
                            color="success" 
                            sx={{ ml: 1, fontSize: '10px', height: '20px' }}
                          />
                        )}
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
                        {plantilla.titulo_certificado || plantilla.tipo_certificado || 'Sin título'}
                      </TableCell>
                      <TableCell align="center">
                        {esVigente ? (
                          <Chip
                            label="Vigente"
                            color="success"
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        ) : (
                          <Chip label="Histórica" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
                        <div>
                          <div>{formatFecha(plantilla.updatedAt)}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {formatHora(plantilla.updatedAt)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
                        <Stack spacing={1}>
                          <Tooltip 
                            title={plantilla.createdByUser?.nombreCompleto || 'Usuario desconocido'} 
                            arrow
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar 
                                sx={{ 
                                  width: 28, 
                                  height: 28, 
                                  bgcolor: 'primary.main',
                                  fontSize: '12px'
                                }}
                              >
                                {getInitials(plantilla.createdByUser?.nombreCompleto || '??')}
                              </Avatar>
                              <div>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {plantilla.createdByUser?.nombreCompleto || 'Usuario no encontrado'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  <PersonIcon sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                                  Creado el {formatFecha(plantilla.createdAt)}
                                </Typography>
                              </div>
                            </Stack>
                          </Tooltip>

                          {plantilla.updatedByUser && plantilla.updatedBy !== plantilla.createdBy && (
                            <Tooltip 
                              title={plantilla.updatedByUser?.nombreCompleto || 'Usuario desconocido'} 
                              arrow
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar 
                                  sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: 'secondary.main',
                                    fontSize: '12px'
                                  }}
                                >
                                  {getInitials(plantilla.updatedByUser?.nombreCompleto || '??')}
                                </Avatar>
                                <div>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {plantilla.updatedByUser?.nombreCompleto || 'Usuario no encontrado'}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    <EditNoteIcon sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                                    Actualizado el {formatFecha(plantilla.updatedAt)}
                                  </Typography>
                                </div>
                              </Stack>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {/* Vista previa - disponible para todas */}
                          <Tooltip title="Vista previa">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleView(plantilla.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>


                          {/* Editar - SOLO para la vigente */}
                          {esVigente ? (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(plantilla.id)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="No editable (plantilla histórica)">
                              <span>
                                <IconButton size="small" color="primary" disabled>
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar esta plantilla? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          variant={'filled'}
          severity={alert ? 'success' : 'error'}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
          {alertMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

const AdminCertificadoPlantillaWrapper = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Plantillas de Certificación</PageTitle>
      <CertificadoPlantillaPage />
    </>
  );
};

export { AdminCertificadoPlantillaWrapper };
export default AdminCertificadoPlantillaWrapper;